import React, { useEffect, useState } from 'react';
import { Table } from '@mantine/core';
import THead from './THead';
import TBody from './TBody';
import { DataTablesProps, SortDirection } from '.';
import LoaderDataTables from './LoaderDataTables';
import TPagination, { DropdownComponent } from './TPaginantion';

export const defaultSizeTable = [
  {
    label: '10',
    value: 10
  },
  {
    label: '20',
    value: 20
  },
  {
    label: '50',
    value: 50
  },
  {
    label: '100',
    value: 100
  }
];

const DataTables: React.FC<DataTablesProps> = ({
  columns,
  data,
  loading,
  isNumbering = true,
  viewText,
  deleteText,
  editText,
  copyText,
  emptyMessage,
  emptyMessageTitle,
  emptyMessageDescription,
  urlEmptyImage,
  sizing,
  withPagination = true,
  totalData,
  currentSize,
  currentPage,
  handleDelete,
  handleEdit,
  handleView,
  handleCopy,
  handleChangeSize,
  handleChangePage,
  withBulkData = false,
  canView,
  canEdit,
  canDelete,
  canApproved,
  canRejected
}) => {
  const [activePage, setActivePage] = useState<number>(currentPage || 1);
  const [activeSizePage, setActiveSizePage] = useState<number>(currentSize || 10);
  const [sizePage, setSizePage] = useState<DropdownComponent[]>(defaultSizeTable);
  const [sortedData, setSortedData] = useState([...data]);
  const [sortConfig, setSortConfig] = useState<any>({
    key: null,
    direction: 'asc'
  });

  useEffect(() => {
    if (sizing) {
      setSizePage(sizing);
    }
  }, [sizing]);

  useEffect(() => {
    if (handleChangeSize) {
      handleChangeSize(activeSizePage);
    }
  }, [activeSizePage]);

  useEffect(() => {
    if (handleChangePage) {
      handleChangePage(activePage);
    }
  }, [activePage]);

  useEffect(() => {
    let sorted = [...data];
    if (sortConfig.key !== null) {
      sorted.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    setSortedData(sorted);
  }, [data, sortConfig]);

  // Reset to first page when data source changes for local pagination
  useEffect(() => {
    const isServerPaginated = Boolean(handleChangePage || handleChangeSize || withBulkData);
    if (withPagination && !isServerPaginated) {
      setActivePage(1);
    }
  }, [data, handleChangePage, handleChangeSize, withBulkData, withPagination]);

  const onSort = (column: any) => {
    let direction: SortDirection = 'asc';
    if (sortConfig.key === column.id && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: column.id, direction });
  };

  // Local pagination when API does not paginate data
  const startIndex = (activePage - 1) * activeSizePage;
  const endIndex = startIndex + activeSizePage;
  const limitedData = sortedData.slice(startIndex, endIndex);

  // If caller handles pagination (provides handleChangePage/handleChangeSize or withBulkData flag), show given data.
  const isServerPaginated = Boolean(handleChangePage || handleChangeSize || withBulkData);
  const _data = withPagination && !isServerPaginated ? limitedData : sortedData;

  return (
    <>
      <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
        <Table.ScrollContainer minWidth={500}>
          <Table striped highlightOnHover withRowBorders={false}>
            <THead
              data={data}
              columns={columns}
              isNumbering={isNumbering}
              sortConfig={sortConfig}
              onSort={onSort}
            />
            {!loading && (
              <TBody
                activePage={activePage}
                activePageSize={activeSizePage}
                columns={columns}
                data={_data}
                rawData={data}
                isNumbering={isNumbering}
                viewText={viewText}
                editText={editText}
                deleteText={deleteText}
                copyText={copyText}
                handleDelete={handleDelete}
                handleEdit={handleEdit}
                handleView={handleView}
                handleCopy={handleCopy}
                canView={canView}
                canEdit={canEdit}
                canDelete={canDelete}
                canApproved={canApproved}
                canRejected={canRejected}
                emptyMessage={emptyMessage}
                emptyMessageTitle={emptyMessageTitle}
                emptyMessageDescription={emptyMessageDescription}
                urlEmptyImage={urlEmptyImage}
              />
            )}
            {loading && <LoaderDataTables columns={columns} isNumbering={isNumbering} />}
          </Table>
        </Table.ScrollContainer>
      </div>
      {!loading && withPagination && (
        <TPagination
          data={totalData || sortedData.length}
          activeSizePage={activeSizePage}
          setActiveSizePage={(size) => {
            setActiveSizePage(size);
            setActivePage(1);
          }}
          sizePage={sizePage}
          activePage={activePage}
          setActivePage={setActivePage}
        />
      )}
    </>
  );
};

export default DataTables;
